<template>
  <div class="dashboard-container">
    <div class="dashboard-layout">
      <!-- ═══════════════════════════════════════════════════════════════════════ -->
      <!-- SIDEBAR -->
      <!-- ═══════════════════════════════════════════════════════════════════════ -->
      <aside class="dashboard-sidebar">
        <div class="sidebar-content">
          <!-- Date Range Section -->
          <section class="sidebar-section" v-if="!yearCompare">
            <div class="section-header">
              <span class="material-symbols-outlined">date_range</span>
              <h3>Période</h3>
            </div>

            <div class="form-group">
              <label>Date début</label>
              <v-menu
                  ref="queryFromPicker"
                  v-model="queryFromPicker"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                  :disabled="isRealtimePreviewActive"
              >
                <template v-slot:activator="{ on }">
                  <div :class="['date-input', { 'is-disabled': isRealtimePreviewActive }]" v-on="on">
                    <span class="material-symbols-outlined">calendar_today</span>
                    <span class="date-value">{{ query.from || 'Sélectionner' }}</span>
                    <span class="material-symbols-outlined arrow">expand_more</span>
                  </div>
                </template>
                <v-date-picker
                    locale="fr-FR"
                    v-model="query.from"
                    no-title
                    @input="queryFromPicker = false"
                    color="#2d5a3d"
                />
              </v-menu>
            </div>

            <div class="form-group" v-if="isIntervalAllowEndDate()">
              <label>Date fin</label>
              <v-menu
                  ref="queryToPicker"
                  v-model="queryToPicker"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                  :disabled="isRealtimePreviewActive"
              >
                <template v-slot:activator="{ on }">
                  <div :class="['date-input', { 'is-disabled': isRealtimePreviewActive }]" v-on="on">
                    <span class="material-symbols-outlined">calendar_today</span>
                    <span class="date-value">{{ query.to || 'Sélectionner' }}</span>
                    <span class="material-symbols-outlined arrow">expand_more</span>
                  </div>
                </template>
                <v-date-picker
                    locale="fr-FR"
                    v-model="query.to"
                    no-title
                    @input="queryToPicker = false"
                    color="#2d5a3d"
                />
              </v-menu>
            </div>

            <!-- Time Range Slider -->
            <div class="form-group" v-else>
              <label>Plage horaire</label>
              <div class="time-range-display">
                <span class="time-badge">{{ timerange[0] }}h</span>
                <span class="time-separator">→</span>
                <span class="time-badge">{{ timerange[1] }}h</span>
              </div>
              <v-range-slider
                  v-model="timerange"
                  :max="72"
                  :min="0"
                  hide-details
                  color="#2d5a3d"
                  track-color="#e2e8e4"
                  @change="updateTimeRange"
                  :disabled="isRealtimePreviewActive"
              />
            </div>

            <div class="form-group">
              <label>Intervalle</label>
              <div class="select-wrapper">
                <select v-model="query.interval" class="custom-select" :disabled="isRealtimePreviewActive">
                  <option v-for="item in intervalChooser" :key="item.value" :value="item.value">
                    {{ item.label }}
                  </option>
                </select>
                <span class="material-symbols-outlined select-arrow">expand_more</span>
              </div>
            </div>

            <div class="form-group realtime-preview-group">
              <label>Aperçu temps réel</label>
              <div class="realtime-preview-controls">
                <div class="select-wrapper realtime-preview-select">
                  <select v-model="realtimeIntervalType" class="custom-select">
                    <option
                        v-for="item in realtimeIntervalTypeOptions"
                        :key="item.value"
                        :value="item.value"
                    >
                      {{ item.label }}
                    </option>
                  </select>
                  <span class="material-symbols-outlined select-arrow">expand_more</span>
                </div>
                <div class="realtime-preview-slider">
                  <div class="realtime-preview-slider-head">
                    <span>Fenêtre</span>
                    <strong>{{ realtimeWindowSize }} {{ realtimeIntervalType }}</strong>
                  </div>
                  <v-slider
                      :value="realtimeSliderDraftIndex"
                      :min="0"
                      :max="realtimeSliderMax"
                      :step="1"
                      hide-details
                      color="#2d5a3d"
                      track-color="#e2e8e4"
                      @input="onRealtimeSliderInput"
                      @change="onRealtimeSliderChange"
                  />
                </div>
                <button
                    v-if="!isRealtimePreviewActive"
                    class="btn btn-primary realtime-preview-btn"
                    @click="enableRealtimePreview"
                >
                  Enable real-time preview
                </button>
                <button
                    v-else
                    class="btn btn-secondary realtime-preview-btn"
                    @click="disableRealtimePreview"
                >
                  Disable real-time preview
                </button>
              </div>
            </div>

            <slot></slot>
          </section>

          <!-- Year Compare Section -->
          <section class="sidebar-section" v-else>
            <div class="section-header">
              <span class="material-symbols-outlined">compare</span>
              <h3>Comparaison annuelle</h3>
            </div>

            <!-- Year Selection -->
            <div class="form-group" v-if="!query.yearCompareUnique">
              <label>Années à comparer</label>
              <div class="chips-container">
                <div
                    v-for="year in YEARS"
                    :key="year"
                    class="year-chip"
                    :class="{ active: query.yearCompareQuery.years.includes(year) }"
                    @click="toggleYear(year)"
                >
                  {{ year }}
                </div>
              </div>
            </div>

            <div class="form-group" v-else>
              <label>Année de référence</label>
              <div class="select-wrapper">
                <select v-model="query.yearCompareQuery.uniqueYear.year" @change="query.yearCompareQuery.update = 1" class="custom-select">
                  <option v-for="year in YEARS" :key="year" :value="year">{{ year }}</option>
                </select>
                <span class="material-symbols-outlined select-arrow">expand_more</span>
              </div>
            </div>

            <!-- Period Count Slider -->
            <div class="form-group" v-if="query.yearCompareUnique">
              <label>Nombre de périodes</label>
              <div class="slider-with-value">
                <v-slider
                    :value="query.uniqueYearsCount"
                    @input="query.uniqueYearsCount = $event"
                    @change="applySliderChange"
                    :min="1"
                    :max="25"
                    hide-details
                    color="#2d5a3d"
                    track-color="#e2e8e4"
                />
                <span class="slider-value">{{ query.uniqueYearsCount }}</span>
              </div>
            </div>

            <!-- Duration Slider -->
            <div class="form-group" v-if="query.yearCompareUnique && ['minute', 'hour'].includes(query.yearCompareQuery.interval)">
              <label>Durée (jours)</label>
              <div class="slider-with-value">
                <v-slider
                    :value="globalOffsetDays"
                    @input="globalOffsetDays = $event"
                    @change="applyGSliderChange"
                    :min="1"
                    :max="30"
                    hide-details
                    color="#2d5a3d"
                    track-color="#e2e8e4"
                />
                <span class="slider-value">{{ globalOffsetDays }}j</span>
              </div>
            </div>

            <!-- Year Date Pickers -->
            <div class="year-dates-grid" v-if="!query.yearCompareUnique">
              <div v-for="id in query.yearCompareQuery.years" :key="id" class="year-date-card">
                <div class="year-date-header">{{ id }}</div>
                <div class="year-date-inputs">
                  <v-menu v-model="menu['f_' + id]" :close-on-content-click="false" offset-y>
                    <template v-slot:activator="{ on }">
                      <div class="mini-date-input" v-on="on">
                        <span class="material-symbols-outlined">play_arrow</span>
                        <span>{{ fromYearCompareQueryFromDisp[id] || 'Début' }}</span>
                      </div>
                    </template>
                    <v-date-picker
                        v-model="query.yearCompareQuery.from[id]"
                        @input="queryFromPicker = false; query.yearCompareQuery.update = 1;"
                        color="#2d5a3d"
                        no-title
                    />
                  </v-menu>
                  <v-menu v-model="menu['t_' + id]" :close-on-content-click="false" offset-y>
                    <template v-slot:activator="{ on }">
                      <div class="mini-date-input" v-on="on">
                        <span class="material-symbols-outlined">stop</span>
                        <span>{{ fromYearCompareQueryToDisp[id] || 'Fin' }}</span>
                      </div>
                    </template>
                    <v-date-picker
                        v-model="query.yearCompareQuery.to[id]"
                        @input="queryFromPicker = false; query.yearCompareQuery.update = 1;"
                        color="#2d5a3d"
                        no-title
                    />
                  </v-menu>
                </div>
              </div>
            </div>

            <!-- Unique Year Date Pickers -->
            <div class="year-dates-grid" v-else>
              <div v-for="id in query.yearCompareQuery.uniqueYear.count" :key="id" class="year-date-card">
                <div class="year-date-header">
                  <span v-if="['minute', 'hour'].includes(query.yearCompareQuery.interval)">
                    {{ query.yearCompareQuery.uniqueYear.year }} (P{{ id }})
                  </span>
                  <span v-else>{{ query.yearCompareQuery.uniqueYear.year }}</span>
                </div>
                <div class="year-date-inputs">
                  <v-menu v-model="menu['fu_' + query.yearCompareQuery.uniqueYear.year + '_' + id]" :close-on-content-click="true" offset-y>
                    <template v-slot:activator="{ on }">
                      <div class="mini-date-input" v-on="on">
                        <span class="material-symbols-outlined">play_arrow</span>
                        <span>{{ getFromDate(query.yearCompareQuery.uniqueYear.year, id) || 'Début' }}</span>
                      </div>
                    </template>
                    <v-date-picker
                        v-model="query.yearCompareQuery.fromUnique[query.yearCompareQuery.uniqueYear.year + '_' + id]"
                        @input="closeMenu('fu_' + query.yearCompareQuery.uniqueYear.year + '_' + id)"
                        color="#2d5a3d"
                        no-title
                    />
                  </v-menu>
                  <v-menu
                      v-if="!['minute', 'hour'].includes(query.yearCompareQuery.interval)"
                      v-model="menu['tu_' + query.yearCompareQuery.uniqueYear.year + '_' + id]"
                      :close-on-content-click="true"
                      offset-y
                  >
                    <template v-slot:activator="{ on }">
                      <div class="mini-date-input" v-on="on">
                        <span class="material-symbols-outlined">stop</span>
                        <span>{{ getToDate(query.yearCompareQuery.uniqueYear.year, id) || 'Fin' }}</span>
                      </div>
                    </template>
                    <v-date-picker
                        v-model="query.yearCompareQuery.toUnique[query.yearCompareQuery.uniqueYear.year + '_' + id]"
                        @input="closeMenu('tu_' + query.yearCompareQuery.uniqueYear.year + '_' + id)"
                        color="#2d5a3d"
                        no-title
                    />
                  </v-menu>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label>Intervalle</label>
              <div class="select-wrapper">
                <select
                    v-model="query.yearCompareQuery.interval"
                    class="custom-select"
                >
                  <option
                      v-for="item in (query.yearCompareUnique ? intervalChooserYearCompareUnique : intervalChooserYearCompare)"
                      :key="item.value"
                      :value="item.value"
                  >
                    {{ item.label }}
                  </option>
                </select>
                <span class="material-symbols-outlined select-arrow">expand_more</span>
              </div>
            </div>

            <!-- Multi-period Toggle -->
            <div class="toggle-card">
              <div class="toggle-info">
                <span class="material-symbols-outlined">layers</span>
                <span>Comparaison multi-périodes</span>
              </div>
              <label class="toggle-switch">
                <input type="checkbox" v-model="query.yearCompareUnique">
                <span class="toggle-slider"></span>
              </label>
            </div>
          </section>

          <!-- Filters Section -->
          <section class="sidebar-section" v-if="site.type === 'Domaine'">
            <div class="section-header">
              <span class="material-symbols-outlined">filter_alt</span>
              <h3>Filtres</h3>
            </div>

            <div class="form-group" v-for="filterName in filtersName" :key="filterName">
              <label>{{ getFiltreLabel(filterName) }}</label>
              <v-select
                  v-model="filters[filterName]"
                  :items="filtersData[filterName]"
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  outlined
                  dense
                  hide-details
                  class="custom-v-select"
              />
            </div>
          </section>

          <!-- Nearest Years Section -->
          <section class="sidebar-section" v-if="yearCompare && site.is_allow_nearest_years && query.yearCompareUnique">
            <div class="section-header">
              <span class="material-symbols-outlined">timeline</span>
              <h3>Années les plus proches</h3>
            </div>

            <div class="form-group">
              <label>Année de référence</label>
              <div class="select-wrapper">
                <select v-model="nearestYearQuery.referenceYear" class="custom-select">
                  <option v-for="year in YEARS" :key="year" :value="year">{{ year }}</option>
                </select>
                <span class="material-symbols-outlined select-arrow">expand_more</span>
              </div>
            </div>

            <div class="form-group">
              <label>Cépage de référence</label>
              <div class="select-wrapper">
                <select v-model="nearestYearQuery.cepage" class="custom-select">
                  <option value="">Sélectionner</option>
                  <option v-for="cepage in filtersData.cepages" :key="cepage" :value="cepage">{{ cepage }}</option>
                </select>
                <span class="material-symbols-outlined select-arrow">expand_more</span>
              </div>
            </div>

            <div class="toggle-options">
              <label class="toggle-option">
                <input type="checkbox" v-model="nearestYearQuery.hasPrecipitations" :true-value="1" :false-value="0">
                <span class="toggle-option-slider"></span>
                <span class="toggle-option-label">Cumul précipitations</span>
              </label>
              <label class="toggle-option">
                <input type="checkbox" v-model="nearestYearQuery.hasTmax" :true-value="1" :false-value="0">
                <span class="toggle-option-slider"></span>
                <span class="toggle-option-label">Cumul température max</span>
              </label>
              <label class="toggle-option">
                <input type="checkbox" v-model="nearestYearQuery.hasTmoy10" :true-value="1" :false-value="0">
                <span class="toggle-option-slider"></span>
                <span class="toggle-option-label">Cumul température moy > 10°</span>
              </label>
            </div>

            <div class="form-group" v-if="site.events && site.events.names">
              <label>Événements</label>
              <v-select
                  v-model="nearestYearQuery.events"
                  :items="site.events.names"
                  multiple
                  chips
                  small-chips
                  outlined
                  dense
                  hide-details
                  class="custom-v-select"
              />
            </div>

            <button
                class="btn btn-primary btn-block"
                @click="computeNearestYears"
                :disabled="!canComputeNearestYears() || loadingNearestYears"
            >
              <span class="material-symbols-outlined" v-if="!loadingNearestYears">check</span>
              <v-progress-circular v-else indeterminate size="18" width="2" color="white" />
              <span>{{ loadingNearestYears ? 'Calcul...' : 'Valider' }}</span>
            </button>
          </section>
        </div>
      </aside>

      <!-- ═══════════════════════════════════════════════════════════════════════ -->
      <!-- MAIN CONTENT -->
      <!-- ═══════════════════════════════════════════════════════════════════════ -->
      <main class="dashboard-main">
        <!-- Tabs Header -->
        <div class="tabs-header">
          <div class="tabs-list">
            <button
                v-if="$store.state.profile.user.role !== 'user'"
                class="tab-add-btn"
                @click="createVisualizationModal = true"
                title="Nouveau graphique"
            >
              <span class="material-symbols-outlined">add_chart</span>
            </button>
            <button
                v-if="$store.state.profile.user.role !== 'user'"
                class="tab-add-btn tab-import-btn"
                @click="visualizationImportOpen = true"
                title="Importer des graphiques d'un autre univers"
            >
              <span class="material-symbols-outlined">download</span>
            </button>
            <button
                v-for="visualization in filteredVisualizations"
                :key="visualization.id"
                class="tab-btn"
                :class="{ active: activeTab === 'tab-' + visualization.id }"
                @click="activeTab = 'tab-' + visualization.id"
            >
              {{ visualization.name }}
            </button>
          </div>
        </div>

        <!-- Tab Content -->
        <div class="tab-content">
          <template v-for="(visualization, i) in site.visualizations">
            <div
                v-if="activeTab === 'tab-' + visualization.id"
                :key="visualization.id + updateKey"
                class="visualization-panel"
            >
              <!-- Actions Bar -->
              <div class="actions-bar" v-if="$store.state.profile.user.role !== 'user'">
                <div class="actions-left">
                  <h2 class="visualization-title">{{ visualization.name }}</h2>
                </div>
                <div class="actions-right">
                  <button class="action-btn" @click="editVisualizationModal = true" title="Modifier">
                    <span class="material-symbols-outlined">edit</span>
                  </button>
                  <button class="action-btn" @click="printImage" title="Imprimer">
                    <span class="material-symbols-outlined">print</span>
                  </button>
                  <button class="action-btn" @click="WeeklyReport" title="Rapport hebdomadaire">
                    <span class="material-symbols-outlined">summarize</span>
                  </button>
                  <button class="action-btn" @click="updateUser" title="Rafraîchir">
                    <span class="material-symbols-outlined">refresh</span>
                  </button>
                  <button class="action-btn danger" @click="deleteVisualization(visualization)" title="Supprimer">
                    <span class="material-symbols-outlined">delete</span>
                  </button>
                </div>
              </div>

              <!-- Visualization Content -->
              <div class="visualization-content">
                <template v-if="!query.yearCompareUnique || !yearCompare">
                  <Visualization
                      :site="site"
                      :visualization="visualization"
                      :query="query"
                      :filters="filters"
                      :realtime="isRealtimePreviewActive"
                      :intervalType="realtimeIntervalType"
                      :realtimeWindowSize="realtimeWindowSize"
                      :yearCompare="yearCompare"
                      :yearCompareQuery="query.yearCompareQuery"
                      :uniqueYearCompare="false"
                      :useAnimatedGraph="animatedGraph"
                      :reload="reload"
                      :childIdsToName="childIdsToName"
                      :loader="loaded"
                  >
                    <p hidden id="graphName">{{ visualization.name }}</p>
                  </Visualization>
                </template>
                <template v-else>
                  <template v-if="['minute', 'hour'].includes(query.yearCompareQuery.interval)">
                    <Visualization
                        :site="site"
                        :visualization="visualization"
                        :query="query"
                        :filters="filters"
                        :realtime="isRealtimePreviewActive"
                        :intervalType="realtimeIntervalType"
                        :realtimeWindowSize="realtimeWindowSize"
                        :yearCompare="yearCompare"
                        :yearCompareQuery="query.yearCompareQuery"
                        :uniqueYearCompare="query.yearCompareUnique"
                        :currentYearIndex="1"
                        :reload="reload"
                        :childIdsToName="childIdsToName"
                    />
                  </template>
                  <template v-else>
                    <div class="multi-visualization-grid">
                      <Visualization
                          v-for="ccount in query.yearCompareQuery.uniqueYear.count"
                          :key="ccount"
                          :site="site"
                          :visualization="visualization"
                          :query="query"
                          :filters="filters"
                          :realtime="isRealtimePreviewActive"
                          :intervalType="realtimeIntervalType"
                          :realtimeWindowSize="realtimeWindowSize"
                          :yearCompare="yearCompare"
                          :yearCompareQuery="query.yearCompareQuery"
                          :uniqueYearCompare="query.yearCompareUnique"
                          :currentYearIndex="ccount"
                          :reload="reload"
                          :childIdsToName="childIdsToName"
                      >
                        <p hidden id="graphName">{{ visualization.name }}</p>
                      </Visualization>
                    </div>
                  </template>
                </template>
              </div>

              <!-- Edit Modal -->
              <v-dialog v-model="editVisualizationModal" max-width="1400px">
                <VisualizationForm
                    ref="vizUpdate"
                    v-model="site.visualizations[i]"
                    :site="site"
                    :clientSites="clientSites"
                    :childIdsToName="childIdsToName"
                    @updated="onVisualizationUpdated"
                    @cancel="onVisualizationCanceled"
                    :key="visualization.id"
                />
              </v-dialog>
            </div>
          </template>
        </div>

        <!-- Nearest Years Tables -->
        <div class="nearest-years-section" v-if="Object.keys(nearestYears).length > 0">
          <NearestYearsTables :nearestYears="nearestYears" />
        </div>
      </main>
    </div>

    <VisualizationImportDialog
        :open="visualizationImportOpen"
        :site="site"
        :clientSites="clientSites"
        @imported="onVisualizationsImported"
        @close="visualizationImportOpen = false"
    />

    <!-- Create Visualization Modal -->
    <v-dialog v-model="createVisualizationModal" content-class="v-dialog--custom" max-width="1400px">
      <VisualizationForm
          ref="vizCreate"
          :value="emptyVisualization"
          :site="site"
          :clientSites="clientSites"
          :childIdsToName="childIdsToName"
          @created="onVisualizationCreated"
          @cancel="onVisualizationCanceled"
      />
    </v-dialog>
  </div>
</template>

<script>
import Visualization from "@/components/Visualization";
import VisualizationForm from "@/components/VisualizationForm";
import VisualizationImportDialog from "@/components/VisualizationImportDialog";
import NearestYearsTables from "@/components/NearestYearsTables";
import { convertDatePickerToDDMMstr, generateArrayOfYears } from "@/utils/dates";
import { buildQueryMust } from "@/elastic/queries";
import Vue from "vue";

Vue.use(require('vue-moment'));

const YEARS = generateArrayOfYears();
const TIMERANGE_DEFAULT = [0, 48];

const YEAR_COMPARE_QUERY_DEFAULT = {
  from: { 2010: "2010-01-01", 2011: "2011-01-01" },
  to: { 2010: "2010-12-31", 2011: "2011-12-31" },
  years: ["2010", "2011"],
  interval: "month",
  uniqueYear: { year: 1970, count: 1 },
  fromUnique: {},
  toUnique: {},
};

const QUERY_DEFAULT = {
  from: Vue.moment().subtract(10, "days").format("YYYY-MM-DD"),
  to: Vue.moment().format("YYYY-MM-DD"),
  timerange: TIMERANGE_DEFAULT,
  interval: "day",
  yearCompareQuery: { ...YEAR_COMPARE_QUERY_DEFAULT },
};

export default {
  name: 'Dashboard',
  components: { Visualization, VisualizationForm, NearestYearsTables, VisualizationImportDialog },

  props: {
    site: { type: Object, required: true },
    clientSites: { type: Array, required: true },
    childIdsToName: { type: Object, required: true },
    yearCompare: { type: Boolean, default: false },
    reload: { type: Boolean, default: false },
    updateKey: { type: Number, default: 0 },
  },

  data() {
    return {
      ready: false,
      loaded: true,
      filtersName: ["parcelles", "cepages", "traitements"],
      filtersData: {
        parcelles: [],
        cepages: [],
        traitements: ["Anti excoriose", "Anti Mildiou", "Anti Oidium", "Anti Botytis", "Ferti", "Insecticide", "Adjuvant", "Anti Botrytis", "Tisane"],
      },
      filters: {},
      animatedGraph: false,
      realtimePreviewEnabled: false,
      realtimeIntervalType: "minutes",
      realtimeWindowSize: 5,
      realtimeSliderDraftIndex: 0,
      realtimeIntervalTypeOptions: [
        { value: "minutes", label: "minutes" },
        { value: "hours", label: "heures" },
        { value: "days", label: "jours" },
      ],
      query: { ...QUERY_DEFAULT },
      queryFromPicker: false,
      queryToPicker: false,
      timerange: [...TIMERANGE_DEFAULT],
      menu: {},
      globalOffsetDays: 1,
      activeTab: null,
      createVisualizationModal: false,
      visualizationImportOpen: false,
      editVisualizationModal: false,
      YEARS: [],
      emptyVisualization: { name: "Graph", component: "", sensors: "", events: [], axes: { axes: [] } },
      nearestYearQuery: {
        referenceYear: "2020",
        cepage: "",
        hasPrecipitations: false,
        hasTmax: false,
        hasTmoy10: false,
        events: [],
      },
      loadingNearestYears: false,
      nearestYears: {},
      intervalChooser: [
        { value: "data", label: "Données" },
        { value: "minute", label: "Minute" },
        { value: "hour", label: "Heure" },
        { value: "day", label: "Jour" },
        { value: "week", label: "Semaine" },
        { value: "month", label: "Mois" },
        { value: "year", label: "Année" },
      ],
      intervalChooserYearCompare: [
        { value: "day", label: "Jour" },
        { value: "week", label: "Semaine" },
        { value: "month", label: "Mois" },
        { value: "year", label: "Année" },
      ],
      intervalChooserYearCompareUnique: [
        { value: "minute", label: "Minute" },
        { value: "hour", label: "Heure" },
        { value: "day", label: "Jour" },
        { value: "week", label: "Semaine" },
        { value: "month", label: "Mois" },
        { value: "year", label: "Année" },
      ],
    };
  },

  computed: {
    filteredVisualizations() {
      return this.site.visualizations.filter(v => !v.component?.includes('RadarChart'));
    },
    isRealtimePreviewActive() {
      return this.realtimePreviewEnabled === true;
    },
    realtimeWindowChoices() {
      const intervalType = this.normalizeRealtimeIntervalType(this.realtimeIntervalType);
      if (intervalType === "minutes") return [5, 15, 30];
      if (intervalType === "hours") return [5, 10, 15, 20, 25, 30];
      return Array.from({ length: 29 }, (_, index) => index + 2);
    },
    realtimeSliderMax() {
      return Math.max(this.realtimeWindowChoices.length - 1, 0);
    },
    elastic_winery_mesures_index() {
      return `winery_mesures_${this.site.client_id}`;
    },
    fromYearCompareQueryFromDisp() {
      const result = {};
      for (const year of this.query.yearCompareQuery.years) {
        if (this.query.yearCompareQuery.from[year]) {
          result[year] = convertDatePickerToDDMMstr(this.query.yearCompareQuery.from[year]);
        }
      }
      return result;
    },
    fromYearCompareQueryToDisp() {
      const result = {};
      for (const year of this.query.yearCompareQuery.years) {
        if (this.query.yearCompareQuery.to[year]) {
          result[year] = convertDatePickerToDDMMstr(this.query.yearCompareQuery.to[year]);
        }
      }
      return result;
    },
  },

  watch: {
    query: { deep: true, handler() { this.saveUserAndVisualizations(); } },
    filters: { deep: true, handler() { this.saveUserAndVisualizations(); } },
    yearCompare(newValue) {
      if (newValue) {
        this.realtimePreviewEnabled = false;
      }
    },
    realtimeIntervalType(newValue) {
      const normalized = this.normalizeRealtimeIntervalType(newValue);
      if (normalized !== newValue) {
        this.realtimeIntervalType = normalized;
        return;
      }
      this.syncRealtimeWindowSizeWithType();
      this.syncRealtimeSliderDraftFromWindowSize();
    },
    realtimeWindowSize(newValue) {
      const normalized = this.normalizeRealtimeWindowSize(newValue);
      if (normalized !== newValue) {
        this.realtimeWindowSize = normalized;
        return;
      }
      this.syncRealtimeSliderDraftFromWindowSize();
    },
    'query.yearCompareQuery.update'(val) {
      if (val === 1) this.handleYearCompareUpdate();
    },
  },

  mounted() {
    this.initializeQuery();
    this.syncRealtimeWindowSizeWithType();
    this.syncRealtimeSliderDraftFromWindowSize();
    if (this.site.type === "Domaine") {
      this.getParcelles();
      this.getCepages();
    }
    if (this.site.is_allow_nearest_years) {
      this.getCepages();
    }
    this.YEARS = generateArrayOfYears();
  },

  methods: {
    // ═══════════════════════════════════════════════════════════════════════════
    // INITIALIZATION
    // ═══════════════════════════════════════════════════════════════════════════

    initializeQuery() {
      const viz = this.$store.state.profile.user.visualizations;
      if (viz?.[this.site.id]?.from) {
        this.query = { ...viz[this.site.id] };
        if (!this.query.yearCompareQuery) {
          this.query.yearCompareQuery = { ...YEAR_COMPARE_QUERY_DEFAULT };
        }
      }

      this.validateTimerange();
      this.initializeYearCompareQuery();

      if (this.filteredVisualizations.length > 0) {
        this.activeTab = `tab-${this.filteredVisualizations[0].id}`;
      }
    },

    validateTimerange() {
      if (!Array.isArray(this.query.timerange) || this.query.timerange.length !== 2) {
        this.query.timerange = [...TIMERANGE_DEFAULT];
      }
      this.timerange = [...this.query.timerange];
    },

    initializeYearCompareQuery() {
      const ycq = this.query.yearCompareQuery;
      if (!ycq.years) return;

      ycq.uniqueYear = ycq.uniqueYear || { year: 1970, count: 1 };
      ycq.fromUnique = ycq.fromUnique || {};
      ycq.toUnique = ycq.toUnique || {};
      ycq.update = 0;

      this.handleYearCompareUpdate();
    },

    handleYearCompareUpdate() {
      const ycq = this.query.yearCompareQuery;
      ycq.update = 0;

      let minDate = new Date(1970, 11, 31);
      let maxDate = new Date(1970, 0, 1);

      for (const year of ycq.years || []) {
        ycq.from[year] = ycq.from[year] || `${year}-01-01`;
        ycq.to[year] = ycq.to[year] || `${year}-12-29`;

        const [, sm, sd] = ycq.from[year].split('-');
        const [, em, ed] = ycq.to[year].split('-');
        const start = new Date(1970, parseInt(sm) - 1, parseInt(sd));
        const end = new Date(1970, parseInt(em) - 1, parseInt(ed));

        if (start < minDate) minDate = start;
        if (end > maxDate) maxDate = end;
      }

      ycq.minDate = { mn: this.formatDate(minDate), year: 1970 };
      ycq.maxDate = { mx: this.formatDate(maxDate), year: 1970 };

      // Initialize unique year dates
      for (let id = 1; id <= ycq.uniqueYear.count; id++) {
        const key = `${ycq.uniqueYear.year}_${id}`;
        ycq.fromUnique[key] = ycq.fromUnique[key] || `${ycq.uniqueYear.year}-01-01`;
        ycq.toUnique[key] = ycq.toUnique[key] || `${ycq.uniqueYear.year}-12-29`;
        this.menu[`fu_${key}`] = this.menu[`fu_${key}`] ?? false;
        this.menu[`tu_${key}`] = this.menu[`tu_${key}`] ?? false;
      }

      // Filter to only selected years
      ycq.from = this.filterObjectByYears(ycq.from, ycq.years);
      ycq.to = this.filterObjectByYears(ycq.to, ycq.years);
    },

    filterObjectByYears(obj, years) {
      const result = {};
      for (const key in obj) {
        if (years.includes(parseInt(key)) || years.includes(key)) {
          result[key] = obj[key];
        }
      }
      return result;
    },

    formatDate(date) {
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${date.getFullYear()}-${m}-${d}`;
    },

    normalizeRealtimeIntervalType(value) {
      const normalized = String(value || "minutes").toLowerCase();
      if (normalized === "hours" || normalized === "hour") return "hours";
      if (normalized === "days" || normalized === "day") return "days";
      return "minutes";
    },
    normalizeRealtimeWindowSize(value) {
      const numericValue = parseInt(value, 10);
      const normalizedInterval = this.normalizeRealtimeIntervalType(this.realtimeIntervalType);

      if (normalizedInterval === "minutes") {
        return [5, 15, 30].includes(numericValue) ? numericValue : 5;
      }

      if (normalizedInterval === "hours") {
        return [5, 10, 15, 20, 25, 30].includes(numericValue) ? numericValue : 5;
      }

      if (Number.isNaN(numericValue)) {
        return 5;
      }

      return Math.max(2, Math.min(30, numericValue));
    },
    syncRealtimeWindowSizeWithType() {
      const preferredDefault = 5;
      if (this.realtimeWindowChoices.includes(this.realtimeWindowSize)) {
        return;
      }
      if (this.realtimeWindowChoices.includes(preferredDefault)) {
        this.realtimeWindowSize = preferredDefault;
        return;
      }
      this.realtimeWindowSize = this.realtimeWindowChoices[0] || preferredDefault;
    },
    getClampedRealtimeSliderIndex(value) {
      const numericValue = parseInt(value, 10);
      if (Number.isNaN(numericValue)) {
        return 0;
      }
      return Math.max(0, Math.min(this.realtimeSliderMax, numericValue));
    },
    syncRealtimeSliderDraftFromWindowSize() {
      const index = this.realtimeWindowChoices.indexOf(this.realtimeWindowSize);
      this.realtimeSliderDraftIndex = index >= 0 ? index : 0;
    },
    onRealtimeSliderInput(value) {
      this.realtimeSliderDraftIndex = this.getClampedRealtimeSliderIndex(value);
    },
    onRealtimeSliderChange(value) {
      this.realtimeSliderDraftIndex = this.getClampedRealtimeSliderIndex(value);
      const selectedWindow = this.realtimeWindowChoices[this.realtimeSliderDraftIndex];
      if (selectedWindow != null) {
        this.realtimeWindowSize = selectedWindow;
      }
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // EVENT HANDLERS
    // ═══════════════════════════════════════════════════════════════════════════

    toggleYear(yearId) {
      const years = this.query.yearCompareQuery.years;
      const idx = years.indexOf(yearId);
      if (idx >= 0) {
        years.splice(idx, 1);
      } else {
        years.push(yearId);
      }
      this.query.yearCompareQuery.update = 1;
    },

    updateTimeRange() {
      this.query.timerange = [...this.timerange];
    },

    enableRealtimePreview() {
      this.realtimeIntervalType = this.normalizeRealtimeIntervalType(this.realtimeIntervalType);
      this.realtimeWindowSize = this.normalizeRealtimeWindowSize(this.realtimeWindowSize);
      this.realtimePreviewEnabled = true;
    },

    disableRealtimePreview() {
      this.realtimePreviewEnabled = false;
    },

    applySliderChange() {
      this.query.yearCompareQuery.uniqueYear.count = this.query.uniqueYearsCount;
      this.query.yearCompareQuery.update = 1;
    },

    applyGSliderChange() {
      if (!['minute', 'hour'].includes(this.query.yearCompareQuery.interval)) return;

      for (let i = 1; i <= this.query.yearCompareQuery.uniqueYear.count; i++) {
        const key = `${this.query.yearCompareQuery.uniqueYear.year}_${i}`;
        const from = this.query.yearCompareQuery.fromUnique[key];
        if (from) {
          const toDate = new Date(from);
          toDate.setDate(toDate.getDate() + this.globalOffsetDays);
          this.query.yearCompareQuery.toUnique[key] = toDate.toISOString().substring(0, 10);
        }
      }
    },

    getFromDate(year, id) {
      const key = `${year}_${id}`;
      const date = this.query.yearCompareQuery.fromUnique[key];

      // Auto-update end date for minute/hour intervals
      if (date && ['minute', 'hour'].includes(this.query.yearCompareQuery.interval)) {
        const toDate = new Date(date);
        toDate.setDate(toDate.getDate() + this.globalOffsetDays);
        this.query.yearCompareQuery.toUnique[key] = toDate.toISOString().substring(0, 10);
      }

      return date ? convertDatePickerToDDMMstr(date) : '';
    },

    getToDate(year, id) {
      const date = this.query.yearCompareQuery.toUnique[`${year}_${id}`];
      return date ? convertDatePickerToDDMMstr(date) : '';
    },

    closeMenu(name) {
      this.$set(this.menu, name, false);
    },

    isIntervalAllowEndDate() {
      return !["data", "hour", "minute"].includes(this.query.interval);
    },

    getFiltreLabel(name) {
      return name.charAt(0).toUpperCase() + name.slice(1);
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // DATA FETCHING
    // ═══════════════════════════════════════════════════════════════════════════

    async getParcelles() {
      const children = Object.keys(this.childIdsToName);
      const must = buildQueryMust(this.site.id, children);

      try {
        const result = await this.$store.dispatch("elasticSearchRequest", {
          index: this.elastic_winery_mesures_index,
          body: {
            query: { bool: { must } },
            aggs: {
              "Parcelle_name.keyword": { terms: { field: "Parcelle_name.keyword", size: 10000 } },
              "Parcelle.keyword": { terms: { field: "Parcelle.keyword", size: 10000 } },
            },
          },
          size: 0,
          site: this.site.id,
        });

        const aggs = result.data.aggregations;
        const buckets = aggs["Parcelle_name.keyword"].buckets.length > 0
            ? aggs["Parcelle_name.keyword"].buckets
            : aggs["Parcelle.keyword"].buckets;

        this.filtersData.parcelles = buckets.map(b => b.key).sort(new Intl.Collator(undefined, { numeric: true }).compare);
      } catch (err) {
        this.$store.commit("setSnack", { type: "red", msg: err });
      }
    },

    async getCepages() {
      const children = Object.keys(this.childIdsToName);
      const must = buildQueryMust(this.site.id, children);

      try {
        const result = await this.$store.dispatch("elasticSearchRequest", {
          index: this.elastic_winery_mesures_index,
          body: {
            query: { bool: { must } },
            aggs: { "Cepage.keyword": { terms: { field: "Cepage.keyword", size: 10000 } } },
          },
          size: 0,
          site: this.site.id,
        });

        this.filtersData.cepages = result.data.aggregations["Cepage.keyword"].buckets.map(b => b.key);
      } catch (err) {
        this.$store.commit("setSnack", { type: "red", msg: err });
      }
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // SAVE & UPDATE
    // ═══════════════════════════════════════════════════════════════════════════

    async saveUserAndVisualizations() {
      this.validateTimerange();

      const user = this.$store.state.profile.user;
      const visualizations = user.visualizations || {};
      //visualizations[this.site.id] = { ...this.query };
      //user.visualizations = visualizations;

      this.$store.commit('SET_VISUALIZATION', {
        siteId: this.site.id,
        query: this.query
      });

      const data = { ...user };
      if (user.clients) data.clients = user.clients.map(c => c.id);
      if (user.sites) data.sites = user.sites.map(s => s.id);
      if (user.users) data.users = user.users.map(u => u.id);

      try {
        await this.$store.dispatch("userEditRequest", { id: user.id, datas: data });
      } catch (err) {
        if (err.response?.status !== 403) {
          this.$store.commit("setSnack", { type: "red", msg: err.response?.data });
        }
      }
    },

    updateUser() {
      return this.$store.dispatch("profileRequest");
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // VISUALIZATION CRUD
    // ═══════════════════════════════════════════════════════════════════════════

    /** Apres duplication : on recharge la liste comme apres une creation. */
    onVisualizationsImported() {
      this.visualizationImportOpen = false;
      this.$emit("visualizationsChanged");
    },

    onVisualizationCreated() {
      this.createVisualizationModal = false;
      this.$emit("visualizationsChanged");
      this.emptyVisualization = { name: "Graph", component: "", sensors: "", events: [], axes: { axes: [] } };
    },

    onVisualizationUpdated() {
      this.editVisualizationModal = false;
      this.$emit("visualizationsChanged");
    },

    onVisualizationCanceled() {
      this.createVisualizationModal = false;
      this.editVisualizationModal = false;
    },

    async deleteVisualization(visualization) {
      if (!confirm("Êtes-vous sûr de vouloir supprimer ce graphique ?")) return;

      try {
        await this.$store.dispatch("visualizationDeleteRequest", { id: visualization.id });
        this.$store.commit("setSnack", { type: "success", msg: "Graphique supprimé !" });
        const idx = this.site.visualizations.indexOf(visualization);
        if (idx > -1) this.site.visualizations.splice(idx, 1);
      } catch (err) {
        this.$store.commit("setSnack", { type: "red", msg: err.response?.data });
      }
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // PRINT & REPORT
    // ═══════════════════════════════════════════════════════════════════════════

    printImage() {
      const intervalLabels = {
        day: 'jours', minute: 'minutes', data: 'données',
        hour: 'heures', week: 'semaines', month: 'mois', year: 'années'
      };

      const canvas = document.getElementsByTagName("canvas")[0];
      const graphName = document.getElementById('graphName')?.innerHTML || '';
      const imgData = canvas.toDataURL("image/png");
      const intervalFormat = intervalLabels[this.query.interval] || '';

      const win = window.open();
      win.document.write(`
        <h1>${document.title}</h1>
        <h2>${this.site.complete_name}</h2>
        <h3>Titre du graphique : ${graphName}</h3>
        <p>${['jours', 'semaines', 'mois', 'années'].includes(intervalFormat)
          ? `Date de début : ${this.query.from} - Date de fin : ${this.query.to} - Intervalle : ${intervalFormat}`
          : `Date de recherche : ${this.query.from} - Intervalle : ${intervalFormat}`
      }</p>
        <p>Date d'impression : ${new Date().toLocaleDateString()} à ${new Date().toLocaleTimeString()}</p>
        <br><br>
        <img src="${imgData}">
      `);
      win.document.close();
      setTimeout(() => { win.print(); win.close(); }, 100);
    },

    async WeeklyReport() {
      try {
        const old = { ...this.query };

        // Calculate week range
        const today = new Date();
        const dayOfWeek = today.getDay() || 7;
        const monday = new Date(today);
        monday.setDate(today.getDate() - (dayOfWeek - 1));
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);

        const fmt = d => d.toISOString().substring(0, 10);

        // Set temporary query
        this.query.from = fmt(monday);
        this.query.to = fmt(sunday);
        this.query.interval = "day";
        this.query.timerange = [0, 72];

        this.loaded = false;
        await this.wait(4000);
        this.loaded = true;
        await this.wait(1000);

        const canvas = document.getElementsByTagName("canvas")[0];
        const imgData = canvas.toDataURL("image/png");

        if (!window.jspdf?.jsPDF) throw new Error("jsPDF non chargé");

        const { jsPDF } = window.jspdf;
        const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const margin = 10;

        pdf.setFontSize(18);
        pdf.text(this.site.complete_name || "Site", margin, 20);
        pdf.setFontSize(14);
        pdf.text("Rapport Hebdomadaire", margin, 30);
        pdf.setFontSize(12);
        pdf.text(`Semaine du ${fmt(monday)} au ${fmt(sunday)}`, margin, 40);

        const imgWidth = pageWidth - margin * 2;
        const imgHeight = (canvas.height / canvas.width) * imgWidth;
        pdf.addImage(imgData, "PNG", margin, 50, imgWidth, imgHeight);

        // Download
        const blob = pdf.output("blob");
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `rapport_hebdomadaire_${fmt(monday)}_au_${fmt(sunday)}.pdf`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 100);

        // Restore
        Object.assign(this.query, old);
      } catch (err) {
        console.error("WeeklyReport error:", err);
        this.$store.commit("setSnack", { type: "red", msg: err.message });
      }
    },

    wait(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // NEAREST YEARS
    // ═══════════════════════════════════════════════════════════════════════════

    canComputeNearestYears() {
      const q = this.nearestYearQuery;
      return q.hasPrecipitations || q.hasTmax || q.hasTmoy10 || q.events.length > 0;
    },

    async computeNearestYears() {
      const q = this.nearestYearQuery;
      const s = this.site;

      if ((q.hasPrecipitations && !s.precipitations) || (q.hasTmoy10 && !s.tmoy) || (q.hasTmax && !s.tmax)) {
        this.$store.commit("setSnack", {
          type: "red",
          msg: "Veuillez définir les mesures correspondantes au niveau des propriétés du site."
        });
        return;
      }

      this.loadingNearestYears = true;

      const payload = {
        clientId: s.client_id,
        site: s.id,
        startDay: this.query.yearCompareQuery.minDate.mn.substring(5),
        endDay: this.query.yearCompareQuery.maxDate.mx.substring(5),
        operations: [],
        cepage: q.cepage,
        events: q.events,
      };

      if (q.hasPrecipitations) payload.operations.push({ op_type_day: "sum", op_type_year: "sum", field_name: s.precipitations });
      if (q.hasTmax) payload.operations.push({ op_type_day: "max", op_type_year: "sum", field_name: s.tmax });
      if (q.hasTmoy10) payload.operations.push({ op_type_day: "avg", op_type_year: "sum10", field_name: s.tmoy });

      try {
        const result = await this.$store.dispatch("elasticNearestYearsRequest", payload);
        this.processNearestYearsResult(result.data);
      } catch (err) {
        this.$store.commit("setSnack", { type: "red", msg: err });
      } finally {
        this.loadingNearestYears = false;
      }
    },

    processNearestYearsResult(data) {
      const refYear = this.nearestYearQuery.referenceYear;

      if (!(refYear in data) || typeof data[refYear].metrics === "string") {
        this.$store.commit("setSnack", { type: "red", msg: `L'année ${refYear} ne contient pas les données suffisantes.` });
        return;
      }

      const refData = data[refYear];
      const s = this.site;
      const q = this.nearestYearQuery;

      const sortNearest = (arr, refVal) => {
        arr.sort((a, b) => Math.abs(a.value - refVal) - Math.abs(b.value - refVal) || Math.abs(a.year - refYear) - Math.abs(b.year - refYear));
        return arr.slice(0, 4);
      };

      const results = {};

      // Process metrics
      ['precipitations', 'tmax', 'tmoy'].forEach((key, i) => {
        const flags = [q.hasPrecipitations, q.hasTmax, q.hasTmoy10];
        const fields = [s.precipitations, s.tmax, s.tmoy];
        const names = ['Precipitations', 'Tmaxsum', 'Tsum10'];

        if (flags[i] && fields[i]) {
          const arr = Object.entries(data)
              .filter(([, d]) => typeof d.metrics !== "string")
              .map(([year, d]) => ({ year, value: Math.round(d.metrics[fields[i]]) }));
          results[names[i]] = sortNearest(arr, Math.round(refData.metrics[fields[i]]));
        }
      });

      // Process events
      if (q.events.length && typeof refData.events !== "string") {
        const events = {};
        q.events.forEach(event => {
          if (refData.events[event]) {
            events[event] = Object.entries(data)
                .filter(([, d]) => typeof d.events !== "string" && d.events[event])
                .map(([year, d]) => ({
                  year,
                  value: this.compareEventDates(d.events[event], refData.events[event])
                }))
                .sort((a, b) => a.value - b.value || Math.abs(a.year - refYear) - Math.abs(b.year - refYear))
                .slice(0, 4);
          }
        });
        if (Object.keys(events).length) results.events = events;
      }

      this.nearestYears = results;
    },

    compareEventDates(d1, d2) {
      const date1 = new Date(d1);
      const date2 = new Date(d2);
      const dayOfYear = d => Math.floor((d - new Date(d.getFullYear(), 0, 0)) / 86400000);
      return Math.abs(dayOfYear(date1) - dayOfYear(date2));
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════════════════════
   CSS VARIABLES
   ═══════════════════════════════════════════════════════════════════════════════ */
.dashboard-container {
  --color-primary: #2d5a3d;
  --color-primary-light: #3d7a52;
  --color-primary-lighter: #5a9e6f;
  --color-primary-bg: #e8f0eb;
  --color-accent: #5a9e6f;

  --color-surface: #ffffff;
  --color-surface-secondary: #fafbfa;
  --color-surface-hover: #f0f4f1;
  --color-background: #f5f7f6;

  --color-border: #e2e8e4;
  --color-border-light: #f0f4f1;

  --color-text-primary: #1a2e1f;
  --color-text-secondary: #5a6b5e;
  --color-text-muted: #8a998e;

  --color-danger: #dc2626;
  --color-danger-bg: #fef2f2;

  --shadow-sm: 0 1px 2px rgba(45, 90, 61, 0.05);
  --shadow-md: 0 4px 12px rgba(45, 90, 61, 0.08);
  --shadow-lg: 0 12px 40px rgba(45, 90, 61, 0.12);

  --radius-sm: 6px;
  --radius-md: 10px;
  --radius-lg: 16px;

  --transition: 200ms cubic-bezier(0.4, 0, 0.2, 1);

  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: var(--color-background);
  min-height: 100vh;
}

/* ═══════════════════════════════════════════════════════════════════════════════
   LAYOUT
   ═══════════════════════════════════════════════════════════════════════════════ */
.dashboard-layout {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 24px;
  padding: 24px;
  max-width: 1920px;
  margin: 0 auto;
}

@media (max-width: 1200px) {
  .dashboard-layout {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 640px) {
  .dashboard-layout {
    padding: 12px;
    gap: 16px;
  }
}

/* ═══════════════════════════════════════════════════════════════════════════════
   SIDEBAR
   ═══════════════════════════════════════════════════════════════════════════════ */
.dashboard-sidebar {
  position: sticky;
  top: 24px;
  height: fit-content;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
}

@media (max-width: 1200px) {
  .dashboard-sidebar {
    position: static;
    max-height: none;
  }
}

@media (max-width: 640px) {
  .sidebar-section {
    padding: 14px;
  }
}

.sidebar-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sidebar-section {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 20px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--color-border-light);
}

.section-header .material-symbols-outlined {
  font-size: 20px;
  color: var(--color-accent);
}

.section-header h3 {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

/* ═══════════════════════════════════════════════════════════════════════════════
   FORM ELEMENTS
   ═══════════════════════════════════════════════════════════════════════════════ */
.form-group {
  margin-bottom: 16px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 8px;
}

/* Date Input */
.date-input {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: var(--color-surface-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: var(--transition);
}

.date-input:hover {
  border-color: var(--color-primary-lighter);
  background: var(--color-surface);
}

.date-input.is-disabled {
  opacity: 0.55;
  cursor: not-allowed;
  pointer-events: none;
}

.date-input .material-symbols-outlined {
  font-size: 18px;
  color: var(--color-accent);
}

.date-input .material-symbols-outlined.arrow {
  margin-left: auto;
  color: var(--color-text-muted);
}

.date-value {
  font-size: 0.9375rem;
  color: var(--color-text-primary);
}

/* Time Range */
.time-range-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 12px;
}

.time-badge {
  padding: 6px 12px;
  background: var(--color-primary-bg);
  color: var(--color-primary);
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 600;
}

.time-separator {
  color: var(--color-text-muted);
}

/* Select */
.select-wrapper {
  position: relative;
}

.custom-select {
  width: 100%;
  padding: 12px 40px 12px 14px;
  background: var(--color-surface-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-size: 0.9375rem;
  color: var(--color-text-primary);
  cursor: pointer;
  appearance: none;
  transition: var(--transition);
}

.custom-select:hover {
  border-color: var(--color-primary-lighter);
}

.custom-select:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px var(--color-primary-bg);
}

.custom-select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.select-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 20px;
  color: var(--color-text-muted);
  pointer-events: none;
}

.realtime-preview-controls {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
}

.realtime-preview-select {
  width: 100%;
}

.realtime-preview-slider {
  width: 100%;
}

.realtime-preview-slider-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  margin-bottom: 4px;
}

.realtime-preview-slider-head strong {
  color: var(--color-text-primary);
  font-weight: 600;
}

.realtime-preview-btn {
  align-self: flex-start;
  white-space: nowrap;
}

/* Slider with value */
.slider-with-value {
  display: flex;
  align-items: center;
  gap: 12px;
}

.slider-with-value .v-slider {
  flex: 1;
}

.slider-value {
  min-width: 40px;
  padding: 6px 10px;
  background: var(--color-primary-bg);
  color: var(--color-primary);
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  font-weight: 600;
  text-align: center;
}

/* Year Chips */
.chips-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 200px;
  overflow-y: auto;
  padding: 4px;
}

.year-chip {
  padding: 6px 12px;
  background: var(--color-surface-secondary);
  border: 1px solid var(--color-border);
  border-radius: 20px;
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: var(--transition);
}

.year-chip:hover {
  border-color: var(--color-primary-lighter);
}

.year-chip.active {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}

/* Year Date Cards */
.year-dates-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 16px;
}

.year-date-card {
  background: var(--color-surface-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 12px;
}

.year-date-header {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-primary);
  margin-bottom: 8px;
}

.year-date-inputs {
  display: flex;
  gap: 8px;
}

@media (max-width: 360px) {
  .year-date-inputs {
    flex-direction: column;
  }
}

.mini-date-input {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: var(--transition);
}

.mini-date-input:hover {
  border-color: var(--color-primary-lighter);
}

.mini-date-input .material-symbols-outlined {
  font-size: 14px;
  color: var(--color-accent);
}

/* Toggle Card */
.toggle-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px;
  background: var(--color-surface-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  margin-top: 16px;
}

.toggle-info {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.875rem;
  color: var(--color-text-primary);
}

.toggle-info .material-symbols-outlined {
  font-size: 18px;
  color: var(--color-accent);
}

/* Toggle Switch */
.toggle-switch {
  position: relative;
  width: 44px;
  height: 24px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: var(--color-border);
  border-radius: 12px;
  transition: var(--transition);
}

.toggle-slider::before {
  content: '';
  position: absolute;
  width: 20px;
  height: 20px;
  left: 2px;
  top: 2px;
  background: white;
  border-radius: 50%;
  transition: var(--transition);
  box-shadow: var(--shadow-sm);
}

.toggle-switch input:checked + .toggle-slider {
  background: var(--color-primary);
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
}

/* Toggle Options */
.toggle-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}

.toggle-option {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}

.toggle-option input {
  display: none;
}

.toggle-option-slider {
  width: 36px;
  height: 20px;
  background: var(--color-border);
  border-radius: 10px;
  position: relative;
  transition: var(--transition);
  flex-shrink: 0;
}

.toggle-option-slider::before {
  content: '';
  position: absolute;
  width: 16px;
  height: 16px;
  left: 2px;
  top: 2px;
  background: white;
  border-radius: 50%;
  transition: var(--transition);
}

.toggle-option input:checked + .toggle-option-slider {
  background: var(--color-primary);
}

.toggle-option input:checked + .toggle-option-slider::before {
  transform: translateX(16px);
}

.toggle-option-label {
  font-size: 0.875rem;
  color: var(--color-text-secondary);
}

/* ═══════════════════════════════════════════════════════════════════════════════
   MAIN CONTENT
   ═══════════════════════════════════════════════════════════════════════════════ */
.dashboard-main {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
  max-width: 100%;
}

/* Tabs */
.tabs-header {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 8px;
}

.tabs-list {
  display: flex;
  gap: 6px;
  overflow-x: auto;
}

.tab-add-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: var(--color-primary);
  border: none;
  border-radius: var(--radius-md);
  color: white;
  cursor: pointer;
  transition: var(--transition);
  flex-shrink: 0;
}

.tab-add-btn:hover {
  background: var(--color-primary-light);
}

.tab-btn {
  padding: 12px 20px;
  background: transparent;
  border: none;
  border-radius: var(--radius-md);
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: var(--transition);
  white-space: nowrap;
}

.tab-btn:hover {
  background: var(--color-surface-hover);
  color: var(--color-text-primary);
}

.tab-btn.active {
  background: var(--color-primary-bg);
  color: var(--color-primary);
}

/* Tab Content */
.tab-content {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  min-width: 0;
  max-width: 100%;
}

.visualization-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-width: 100%;
}

/* Actions Bar */
.actions-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-border-light);
}

.visualization-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
}

.actions-right {
  display: flex;
  gap: 6px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: var(--color-surface-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: var(--transition);
}

.action-btn:hover {
  background: var(--color-surface-hover);
  border-color: var(--color-primary-lighter);
  color: var(--color-primary);
}

.action-btn.danger:hover {
  background: var(--color-danger-bg);
  border-color: var(--color-danger);
  color: var(--color-danger);
}

.action-btn .material-symbols-outlined {
  font-size: 18px;
}

@media (max-width: 640px) {
  .actions-bar {
    flex-wrap: wrap;
    gap: 10px;
    padding: 12px 14px;
  }

  .actions-right {
    width: 100%;
    justify-content: flex-end;
  }

  .tab-btn {
    padding: 10px 14px;
    font-size: 0.875rem;
  }
}

/* Visualization Content */
.visualization-content {
  padding: 20px;
  min-height: 400px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
}

.multi-visualization-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Nearest Years */
.nearest-years-section {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 20px;
}

@media (max-width: 640px) {
  .visualization-content {
    padding: 12px;
    min-height: 280px;
  }

  .nearest-years-section {
    padding: 14px;
  }
}

/* ═══════════════════════════════════════════════════════════════════════════════
   BUTTONS
   ═══════════════════════════════════════════════════════════════════════════════ */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: var(--radius-md);
  font-size: 0.9375rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: var(--transition);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn .material-symbols-outlined {
  font-size: 18px;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-primary-light);
}

.btn-secondary {
  background: var(--color-surface-secondary);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--color-surface-hover);
  color: var(--color-text-primary);
}

.btn-block {
  width: 100%;
}

/* ═══════════════════════════════════════════════════════════════════════════════
   MATERIAL SYMBOLS
   ═══════════════════════════════════════════════════════════════════════════════ */
.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
}

/* ═══════════════════════════════════════════════════════════════════════════════
   VUETIFY OVERRIDES
   ═══════════════════════════════════════════════════════════════════════════════ */
.custom-v-select >>> .v-input__slot {
  background: var(--color-surface-secondary) !important;
  border-radius: var(--radius-md) !important;
}

.custom-v-select >>> .v-input__slot fieldset {
  border-color: var(--color-border) !important;
}

/* Scrollbar */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: var(--color-surface-secondary);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: var(--color-border);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--color-text-muted);
}

.tab-import-btn {
  margin-left: 4px;
}
</style>
